import React, { useState, useRef, useMemo } from 'react';
import Nav from 'react-bootstrap/Nav';
import TabContainer from 'react-bootstrap/TabContainer';
import Tab from 'react-bootstrap/Tab';
import { Loader } from '../../../components';
import { t } from '../../../i18n';
import chunk from 'lodash/chunk';
import CoursesSlider from './slider/CoursesSlider';
import SliderControls from './slider-controls/SliderControls';
import './styles.scss';

const cardsPerSlide = 3;
const getSlidesCollection = courses => chunk(courses, cardsPerSlide);

const ProfileCoursesCarousel = ({
  topTitle,
  mainTitle,
  mainTitleClassName,
  allCourses,
  activeCourses,
  completedCourses,
  notStartedCourses,
  onPlayCoursePreview,
  isLoading,
}) => {
  const [activeNavigationTab, setActiveNavigationTab] = useState('all-courses');

  const allCoursesCarouselRef = useRef(null);
  const activeCoursesCarouselRef = useRef(null);
  const completedCoursesCarouselRef = useRef(null);
  const notStartedCoursesCarouselRef = useRef(null);

  const [currentAllCoursesSlideIndex, setCurrentAllCoursesSlideIndex] = useState(0);
  const [currentActiveCoursesSlideIndex, setCurrentActiveCoursesSlideIndex] = useState(0);
  const [currentCompletedCoursesSlideIndex, setCurrentCompletedCoursesSlideIndex] = useState(0);
  const [currentNotStartedCoursesSlideIndex, setCurrentNotStartedCoursesSlideIndex] = useState(0);

  const [showAllCourses, setShowAllCourses] = useState(false);
  const [showAllActiveCourses, setShowAllActiveCourses] = useState(false);
  const [showAllCompletedCourses, setShowAllCompletedCourses] = useState(false);
  const [showAllNotStartedCourses, setShowAllNotStartedCourses] = useState(false);

  const allCoursesSlidesCollection = useMemo(() => getSlidesCollection(allCourses), [allCourses]);
  const activeCoursesSlidesCollection = useMemo(() => getSlidesCollection(activeCourses), [activeCourses]);
  const completedCoursesSlidesCollection = useMemo(() => getSlidesCollection(completedCourses), [completedCourses]);
  const notStartedCoursesSlidesCollection = useMemo(() => getSlidesCollection(notStartedCourses), [notStartedCourses]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="profile-courses-carousel">
      <div className="profile-courses-carousel__title">
        <p>{topTitle}</p>
        <h2 className={mainTitleClassName}>{mainTitle}</h2>
      </div>
      <TabContainer defaultActiveKey="all-courses">
        <div className="profile-courses-carousel__navigation-and-controls-wrapper">
          <Nav
            variant="pills"
            className="profile-courses-carousel__navigation-wrapper"
            onSelect={selectedNavigationTab => setActiveNavigationTab(selectedNavigationTab)}
          >
            <Nav.Item>
              <Nav.Link eventKey="all-courses" className="profile-courses-carousel__navigation-link">
                {`${t('DIGITAL_COURSES.PROFILE_SCREEN.ALL')} (${allCourses.length})`}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="active-courses" className="profile-courses-carousel__navigation-link">
                {`${t('DIGITAL_COURSES.PROFILE_SCREEN.ACTIVE')} (${activeCourses.length})`}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="completed-courses" className="profile-courses-carousel__navigation-link">{`${t(
                'DIGITAL_COURSES.PROFILE_SCREEN.COMPLETED',
              )} (${completedCourses.length})`}</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="not-started-courses" className="profile-courses-carousel__navigation-link">{`${t(
                'DIGITAL_COURSES.PROFILE_SCREEN.NOT_STARTED',
              )} (${notStartedCourses.length})`}</Nav.Link>
            </Nav.Item>
          </Nav>
          {activeNavigationTab === 'all-courses' && (
            <SliderControls
              carouselRef={allCoursesCarouselRef}
              slidesCollectionLength={allCoursesSlidesCollection.length}
              activeSlideIndex={currentAllCoursesSlideIndex}
              showAllCourses={showAllCourses}
            />
          )}
          {activeNavigationTab === 'active-courses' && (
            <SliderControls
              carouselRef={activeCoursesCarouselRef}
              slidesCollectionLength={allCoursesSlidesCollection.length}
              activeSlideIndex={currentActiveCoursesSlideIndex}
              showAllCourses={showAllActiveCourses}
            />
          )}
          {activeNavigationTab === 'completed-courses' && (
            <SliderControls
              carouselRef={completedCoursesCarouselRef}
              slidesCollectionLength={allCoursesSlidesCollection.length}
              activeSlideIndex={currentCompletedCoursesSlideIndex}
              showAllCourses={showAllCompletedCourses}
            />
          )}
          {activeNavigationTab === 'not-started-courses' && (
            <SliderControls
              carouselRef={completedCoursesCarouselRef}
              slidesCollectionLength={allCoursesSlidesCollection.length}
              activeSlideIndex={currentNotStartedCoursesSlideIndex}
              showAllCourses={showAllNotStartedCourses}
            />
          )}
        </div>
        <Tab.Content>
          <Tab.Pane eventKey="all-courses">
            <CoursesSlider
              carouselRef={allCoursesCarouselRef}
              courses={allCourses}
              cardsPerSlide={cardsPerSlide}
              slidesCollection={allCoursesSlidesCollection}
              onSlide={slideIndex => {
                setCurrentAllCoursesSlideIndex(slideIndex);
              }}
              onPlayCoursePreview={onPlayCoursePreview}
              onShowAllCourses={() => setShowAllCourses(!showAllCourses)}
              showAllCourses={showAllCourses}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="active-courses">
            <CoursesSlider
              carouselRef={activeCoursesCarouselRef}
              courses={activeCourses}
              cardsPerSlide={cardsPerSlide}
              slidesCollection={activeCoursesSlidesCollection}
              onSlide={slideIndex => {
                setCurrentActiveCoursesSlideIndex(slideIndex);
              }}
              onPlayCoursePreview={onPlayCoursePreview}
              onShowAllCourses={() => setShowAllActiveCourses(!showAllActiveCourses)}
              showAllCourses={showAllCourses}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="completed-courses">
            <CoursesSlider
              carouselRef={completedCoursesCarouselRef}
              courses={completedCourses}
              cardsPerSlide={cardsPerSlide}
              slidesCollection={completedCoursesSlidesCollection}
              onSlide={slideIndex => {
                setCurrentCompletedCoursesSlideIndex(slideIndex);
              }}
              onPlayCoursePreview={onPlayCoursePreview}
              onShowAllCourses={() => setShowAllCompletedCourses(!showAllCompletedCourses)}
              showAllCourses={showAllCourses}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="not-started-courses">
            <CoursesSlider
              carouselRef={notStartedCoursesCarouselRef}
              courses={notStartedCourses}
              cardsPerSlide={cardsPerSlide}
              slidesCollection={notStartedCoursesSlidesCollection}
              onSlide={slideIndex => {
                setCurrentNotStartedCoursesSlideIndex(slideIndex);
              }}
              onPlayCoursePreview={onPlayCoursePreview}
              onShowAllCourses={() => setShowAllNotStartedCourses(!showAllNotStartedCourses)}
              showAllCourses={showAllCourses}
            />
          </Tab.Pane>
        </Tab.Content>
      </TabContainer>
      <CoursesSlider />
    </div>
  );
};

export default ProfileCoursesCarousel;
